import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Zap, Eye, Pencil, Download, Trash2, Copy, Check, X, Calculator, Building2, Plus, Search, MapPin } from 'lucide-react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../lib/firebase';
import { getEstimates, deleteEstimate, updateEstimate } from '../../services/estimateService';
import { getProperties, deleteProperty } from '../../services/propertyService';
import { EstimateWithId } from '../../types/estimation';
import { Property } from '../../types/property';
import { useLoadingState } from '../../hooks/useLoadingState';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import EstimateTypeBadge from './EstimateTypeBadge';
import ConfirmDialog from '../ui/ConfirmDialog';
import { migrationService } from '../../services/estimationMigrationService';
import { getDisplayPrice, formatPrice } from '../../utils/priceCalculator';
import { generateEstimatePDF } from '../../utils/generateEstimatePDF';

const PROPERTY_TYPE_TRANSLATIONS = {
  'house': 'Maison',
  'apartment': 'Appartement',
  'building': 'Immeuble'
} as const;

// ─── Design tokens ────────────────────────────────────────────────────────────

const CARD_STYLE: React.CSSProperties = {
  background: 'white',
  border: '0.5px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  padding: '13px 16px',
};

const formatAddress = (estimate: EstimateWithId) => {
  const location = estimate.type === 'detailed'
    ? estimate.description?.location
    : estimate.location;
  if (!location) return 'Adresse non spécifiée';
  const parts = [location.address, location.postcode, location.city].filter(Boolean);
  return parts.join(' - ');
};

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: '#7C6FA0',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 12,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const TypeIcon: React.FC<{ type: string }> = ({ type }) => (
  <div style={{
    width: 36, height: 36, flexShrink: 0,
    borderRadius: 'var(--radius-md)',
    background: type === 'detailed'
      ? 'linear-gradient(135deg, #EDE9FE, #C4B5FD)'
      : 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    {type === 'detailed'
      ? <FileText size={16} color="#5B21B6" strokeWidth={1.75} />
      : <Zap size={16} color="#92400E" strokeWidth={1.75} />
    }
  </div>
);

interface EstimatesListProps {
  userId?: string;
}

const EstimatesList: React.FC<EstimatesListProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState<EstimateWithId[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const { isLoading, error, setLoading, handleError, clearError } = useLoadingState();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    estimateId: string | null;
  }>({ isOpen: false, estimateId: null });
  const [deletePropertyConfirm, setDeletePropertyConfirm] = useState<{
    isOpen: boolean;
    propertyId: string | null;
  }>({ isOpen: false, propertyId: null });
  const [editingEstimateId, setEditingEstimateId] = useState<string | null>(null);
  const [currentCommentValue, setCurrentCommentValue] = useState<string>('');
  const [priceCache, setPriceCache] = useState<Map<string, number>>(new Map());
  const [addressFilter, setAddressFilter] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    clearError();

    const estimatesRef = ref(db, `estimates/${userId}`);
    const unsubscribeEstimates = onValue(estimatesRef, (snapshot) => {
      if (snapshot.exists()) {
        const estimatesData = Object.entries(snapshot.val()).map(([id, data]) => {
          const { id: _, ...cleanData } = data as any;
          return { ...cleanData, id };
        });

        const uniqueEstimates = Array.from(
          new Map(estimatesData.map(e => [e.id, e])).values()
        );

        if (uniqueEstimates.length !== estimatesData.length) {
          console.warn('Duplicated estimates detected and removed:', {
            total: estimatesData.length,
            unique: uniqueEstimates.length
          });
        }

        setEstimates(uniqueEstimates.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
      } else {
        setEstimates([]);
      }
      setLoading(false);
    }, (error) => {
      console.error('Erreur lors de l\'écoute des estimations:', error);
      handleError(error);
      setLoading(false);
    });

    const propertiesRef = ref(db, `properties/${userId}`);
    const unsubscribeProperties = onValue(propertiesRef, (snapshot) => {
      if (snapshot.exists()) {
        const propertiesData = Object.values(snapshot.val()) as Property[];
        setProperties(propertiesData.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));
      } else {
        setProperties([]);
      }
    }, (error) => {
      console.error('Erreur lors de l\'écoute des propriétés:', error);
    });

    return () => {
      unsubscribeEstimates();
      unsubscribeProperties();
    };
  }, [userId]);

  useEffect(() => {
    const cache = new Map<string, number>();
    estimates.forEach(estimate => {
      if (estimate.id) {
        const priceResult = getDisplayPrice(estimate);
        cache.set(estimate.id, priceResult.displayPrice);
      }
    });
    setPriceCache(cache);
  }, [estimates]);

  // Estimations detailed dont l'adresse ne correspond à aucune Property en base
  const orphanEstimates = useMemo(() => {
    return estimates.filter(e => {
      if (e.type !== 'detailed') return false;
      const loc = (e as any).description?.location;
      if (!loc?.address || !loc?.postcode) return false;
      const hasProperty = properties.some(
        p => p.address === loc.address && p.postcode === loc.postcode
      );
      return !hasProperty;
    });
  }, [estimates, properties]);

  const distinctAddresses = useMemo(() => {
    const addressMap = new Map<string, string>();
    estimates.forEach(e => {
      const addr = formatAddress(e);
      const date = (e as any).updatedAt || e.createdAt;
      if (!addressMap.has(addr) || date > (addressMap.get(addr) ?? '')) {
        addressMap.set(addr, date);
      }
    });
    return Array.from(addressMap.entries())
      .sort((a, b) => b[1].localeCompare(a[1]))
      .map(([addr]) => addr);
  }, [estimates]);

  const dropdownAddresses = useMemo(() => {
    if (!addressFilter.trim()) return distinctAddresses;
    const lower = addressFilter.toLowerCase();
    return distinctAddresses.filter(a => a.toLowerCase().includes(lower));
  }, [distinctAddresses, addressFilter]);

  const filteredEstimates = useMemo(() => {
    if (!addressFilter.trim()) return estimates;
    const lower = addressFilter.toLowerCase();
    return estimates.filter(e => formatAddress(e).toLowerCase().includes(lower));
  }, [estimates, addressFilter]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteClick = (estimateId: string) => {
    setDeleteConfirmation({ isOpen: true, estimateId });
  };

  const handleConfirmDelete = async () => {
    if (!userId || !deleteConfirmation.estimateId) return;
    try {
      setLoading(true);
      clearError();
      await deleteEstimate(userId, deleteConfirmation.estimateId);
      setEstimates(estimates.filter(e => e.id !== deleteConfirmation.estimateId));
      setDeleteConfirmation({ isOpen: false, estimateId: null });
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePropertyClick = (propertyId: string) => {
    setDeletePropertyConfirm({ isOpen: true, propertyId });
  };

  const handleConfirmDeleteProperty = async (propertyId: string | null) => {
    if (!userId || !propertyId) return;
    try {
      setLoading(true);
      clearError();
      // Supprimer le bien
      await deleteProperty(userId, propertyId);
      // Supprimer toutes les estimations liées à ce bien
      const linkedEstimates = estimates.filter(e => (e as any).propertyId === propertyId);
      await Promise.all(linkedEstimates.map(e => deleteEstimate(userId, e.id)));
      // Mettre à jour l'état local
      setProperties(properties.filter(p => p.id !== propertyId));
      setEstimates(estimates.filter(e => (e as any).propertyId !== propertyId));
      setDeletePropertyConfirm({ isOpen: false, propertyId: null });
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewEstimate = async (estimate: EstimateWithId) => {
    try {
      const preparedData = migrationService.prepareForDisplay(estimate);
      if (userId && estimate.id) {
        if (!estimate.type && preparedData.type) {
          await updateEstimate(userId, estimate.id, { type: preparedData.type });
        }
        if (preparedData._needsMigration) {
          await migrationService.migrateAndSave(userId, estimate.id, estimate);
        }
      }
      localStorage.setItem('viewEstimate', JSON.stringify(preparedData));
      navigate(`/estimation/${estimate.id}/view`);
    } catch (error) {
      console.error('Error preparing estimate for view:', error);
      localStorage.setItem('viewEstimate', JSON.stringify(estimate));
      navigate(`/estimation/${estimate.id}/view`);
    }
  };

  const handleEditEstimate = async (estimate: EstimateWithId) => {
    try {
      const preparedData = migrationService.prepareForDisplay(estimate);
      if (userId && preparedData._needsMigration && estimate.id) {
        await migrationService.migrateAndSave(userId, estimate.id, estimate);
      }
      localStorage.setItem('editEstimate', JSON.stringify({
        ...preparedData,
        _editMode: true,
        _estimateId: estimate.id
      }));
      navigate(`/estimation/${estimate.id}/edit`);
    } catch (error) {
      console.error('Error preparing estimate for edit:', error);
      localStorage.setItem('editEstimate', JSON.stringify({
        ...estimate,
        _editMode: true,
        _estimateId: estimate.id
      }));
      navigate(`/estimation/${estimate.id}/edit`);
    }
  };

  const handleDuplicateEstimate = (estimate: EstimateWithId) => {
    try {
      const preparedData = migrationService.prepareForDisplay(estimate);
      const { id, propertyId, createdAt, updatedAt, estimateId, _editMode, _estimateId, _needsMigration, ...dataToDuplicate } = preparedData as any;
      localStorage.setItem('editEstimate', JSON.stringify({ ...dataToDuplicate, _editMode: false }));
      navigate('/estimation/nouvelle');
    } catch (error) {
      console.error('Error preparing estimate for duplication:', error);
      const { id, propertyId, createdAt, updatedAt, estimateId, ...dataToDuplicate } = estimate as any;
      localStorage.setItem('editEstimate', JSON.stringify({ ...dataToDuplicate, _editMode: false }));
      navigate('/estimation/nouvelle');
    }
  };


  const handleEditComment = (estimateId: string, currentComment: string) => {
    setEditingEstimateId(estimateId);
    setCurrentCommentValue(currentComment || '');
  };

  const handleSaveComment = async () => {
    if (!userId || !editingEstimateId) return;
    try {
      setLoading(true);
      clearError();
      await updateEstimate(userId, editingEstimateId, { comment: currentCommentValue });
      setEstimates(estimates.map(e =>
        e.id === editingEstimateId ? { ...e, comment: currentCommentValue } : e
      ));
      setEditingEstimateId(null);
      setCurrentCommentValue('');
    } catch (error) {
      console.error('Error saving comment:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingEstimateId(null);
    setCurrentCommentValue('');
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  if (estimates.length === 0 && properties.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Aucune estimation ni bien trouvé</p>
        <div className="mt-4 flex justify-center gap-3">
          <Link to="/quick-estimate" className="btn btn-secondary inline-flex items-center gap-2">
            <Calculator size={15} />
            Estimation rapide
          </Link>
          <Link to="/estimation/nouvelle" className="btn btn-primary inline-flex items-center gap-2">
            <FileText size={15} />
            Estimation détaillée
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Mes biens ─────────────────────────────────────────────────────── */}
      {(properties.length > 0 || orphanEstimates.length > 0) && (
        <div className="mb-8">
          <div className="flex flex-col gap-2">
            {properties.map((property) => (
              <div key={property.id} style={CARD_STYLE}>
                <div className="flex items-center gap-3">
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, flexShrink: 0,
                    borderRadius: 'var(--radius-md)',
                    background: 'linear-gradient(135deg, #F1F5F9, #CBD5E1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Building2 size={16} color="#475569" strokeWidth={1.75} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 13, fontWeight: 500, color: '#1E1148' }}>
                      {PROPERTY_TYPE_TRANSLATIONS[property.type as keyof typeof PROPERTY_TYPE_TRANSLATIONS] || property.type}
                    </p>
                    <p style={{ fontSize: 12, color: '#7C6FA0', marginTop: 1 }}>
                      {property.address}, {property.postcode} {property.city} · {property.area} m²
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleDeletePropertyClick(property.id)}
                      className="icon-btn"
                      title="Supprimer ce bien"
                      style={{ color: '#9CA3AF' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#9CA3AF')}
                    >
                      <Trash2 size={13} />
                    </button>
                    <Link
                      to="/estimation/nouvelle"
                      onClick={(e) => {
                        e.preventDefault();
                        localStorage.setItem('editEstimate', JSON.stringify({
                          type: 'detailed',
                          propertyId: property.id,
                          description: {
                            location: {
                              address: property.address,
                              postcode: property.postcode,
                              city: property.city
                            },
                            propertyType: property.type,
                            area: property.area
                          }
                        }));
                        navigate('/estimation/nouvelle');
                      }}
                      className="btn btn-primary flex items-center gap-2 text-sm"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                    >
                      <Plus size={13} />
                      {property.estimateId ? 'Ré-estimer' : 'Estimer'}
                    </Link>
                  </div>
                </div>
              </div>
            ))}

            {/* ── Biens orphelins (estimations detailed sans Property en base) ── */}
            {orphanEstimates.map((estimate) => {
              const loc = (estimate as any).description?.location;
              const propType = (estimate as any).description?.propertyType;
              const area = (estimate as any).description?.area;
              return (
                <div key={estimate.id} style={CARD_STYLE}>
                  <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div style={{
                      width: 36, height: 36, flexShrink: 0,
                      borderRadius: 'var(--radius-md)',
                      background: 'linear-gradient(135deg, #F1F5F9, #CBD5E1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Building2 size={16} color="#475569" strokeWidth={1.75} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#1E1148' }}>
                        {PROPERTY_TYPE_TRANSLATIONS[propType as keyof typeof PROPERTY_TYPE_TRANSLATIONS] || propType || 'Bien'}
                      </p>
                      <p style={{ fontSize: 12, color: '#7C6FA0', marginTop: 1 }}>
                        {[loc?.address, loc?.postcode, loc?.city].filter(Boolean).join(', ')}
                        {area ? ` · ${area} m²` : ''}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleDeleteClick(estimate.id)}
                        className="icon-btn"
                        title="Supprimer ce bien"
                        style={{ color: '#9CA3AF' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#9CA3AF')}
                      >
                        <Trash2 size={13} />
                      </button>
                      <button
                        onClick={() => handleEditEstimate(estimate)}
                        className="btn btn-primary flex items-center gap-2 text-sm"
                        style={{ padding: '6px 12px', fontSize: 12 }}
                      >
                        <Plus size={13} />
                        Estimer
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Estimations réalisées ─────────────────────────────────────────── */}
      {estimates.length > 0 && (
        <div>
          <p style={SECTION_LABEL_STYLE}>Estimations réalisées</p>

          {/* Address filter */}
          <div ref={filterRef} style={{ position: 'relative', marginBottom: 12 }}>
            <Search size={14} color="#7C6FA0" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              type="text"
              value={addressFilter}
              onChange={e => { setAddressFilter(e.target.value); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Filtrer par adresse..."
              style={{ width: '100%', padding: '9px 36px', fontSize: 13, border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-md)', outline: 'none', boxSizing: 'border-box', color: 'var(--color-text-primary)' }}
            />
            {addressFilter && (
              <button
                onClick={() => { setAddressFilter(''); setDropdownOpen(false); }}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
              >
                <X size={14} color="#7C6FA0" />
              </button>
            )}
            {dropdownOpen && dropdownAddresses.length > 0 && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'white', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-card)', zIndex: 50, maxHeight: 220, overflowY: 'auto' }}>
                {dropdownAddresses.map(addr => (
                  <button
                    key={addr}
                    onClick={() => { setAddressFilter(addr); setDropdownOpen(false); }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#F5F3FF')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'transparent', border: 'none', borderBottom: '0.5px solid var(--color-border)', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--color-text-primary)' }}
                  >
                    <MapPin size={12} color="#7C6FA0" style={{ flexShrink: 0 }} />
                    {addr}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filteredEstimates.length === 0 && addressFilter && (
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontStyle: 'italic', padding: '8px 0' }}>
              Aucune estimation trouvée pour cette adresse.
            </p>
          )}

          <div className="flex flex-col gap-2">
            {filteredEstimates.map((estimate) => {
              const priceResult = getDisplayPrice(estimate);
              const displayPrice = priceCache.get(estimate.id) || priceResult.displayPrice;
              const estimateType = estimate.type || 'quick';

              return (
                <div key={estimate.id} style={CARD_STYLE}>
                  {/* ── Main row ── */}
                  <div className="flex items-center gap-3">
                    {/* Type icon */}
                    <TypeIcon type={estimateType} />

                    {/* Address + badge + date */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span style={{
                          fontSize: 13, fontWeight: 500, color: '#1E1148',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {formatAddress(estimate)}
                        </span>
                        <EstimateTypeBadge type={estimateType as 'detailed' | 'quick'} />
                        {estimate.status === 'draft' && (
                          <span style={{
                            fontSize: 10, fontWeight: 500,
                            background: '#FEF3C7', color: '#92400E',
                            padding: '2px 7px', borderRadius: 99,
                            whiteSpace: 'nowrap', flexShrink: 0, lineHeight: '16px',
                          }}>
                            Brouillon
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 11, color: '#7C6FA0', marginTop: 2 }}>
                        Créée le {new Date(estimate.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="shrink-0 text-right" style={{ minWidth: 90 }}>
                      {displayPrice > 0 && (
                        <>
                          <p style={{ fontSize: 14, fontWeight: 500, color: '#1E1148' }}>
                            {formatPrice(displayPrice)}
                          </p>
                          {priceResult.isRecalculated && (
                            <p style={{ fontSize: 10, color: '#D97706', marginTop: 1 }}>
                              ⚠ recalculé
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleViewEstimate(estimate)}
                        className="icon-btn"
                        title="Voir"
                      >
                        <Eye size={13} />
                      </button>
                      {estimateType === 'detailed' && (
                        <button
                          onClick={() => handleEditEstimate(estimate)}
                          className="icon-btn"
                          title="Modifier"
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => generateEstimatePDF(estimate)}
                        className="icon-btn"
                        title="Télécharger PDF"
                      >
                        <Download size={13} />
                      </button>
                      {estimateType === 'detailed' && (
                        <button
                          onClick={() => handleDuplicateEstimate(estimate)}
                          className="icon-btn"
                          title="Dupliquer"
                        >
                          <Copy size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(estimate.id)}
                        className="icon-btn icon-btn-danger"
                        title="Supprimer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* ── Comment row ── */}
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--color-border)' }}>
                    {editingEstimateId === estimate.id ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={currentCommentValue}
                          onChange={(e) => setCurrentCommentValue(e.target.value)}
                          placeholder="Ajouter un commentaire…"
                          className="w-full px-3 py-2 rounded-md resize-none"
                          style={{
                            border: '0.5px solid var(--color-border)',
                            fontSize: 12,
                            outline: 'none',
                            color: '#1E1148',
                          }}
                          rows={2}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveComment}
                            className="flex items-center gap-1 px-3 py-1 rounded text-white text-xs"
                            style={{ background: '#16A34A', fontSize: 11 }}
                          >
                            <Check size={11} />
                            Enregistrer
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center gap-1 px-3 py-1 rounded text-xs"
                            style={{ background: '#F1F5F9', color: '#475569', fontSize: 11 }}
                          >
                            <X size={11} />
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => handleEditComment(estimate.id, estimate.comment || '')}
                        className="cursor-pointer rounded px-1 -mx-1"
                        style={{ transition: 'background 0.15s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#F8F6FF')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {estimate.comment ? (
                          <p style={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap' }}>
                            {estimate.comment}
                          </p>
                        ) : (
                          <p style={{ fontSize: 11, color: '#B8B0CC', fontStyle: 'italic' }}>
                            Cliquer pour ajouter un commentaire…
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmation.isOpen}
        title="Confirmer la suppression"
        message="Êtes-vous sûr de vouloir supprimer cette estimation ? Cette action est irréversible."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmation({ isOpen: false, estimateId: null })}
        variant="danger"
      />
      <ConfirmDialog
        isOpen={deletePropertyConfirm.isOpen}
        title="Supprimer ce bien ?"
        message="Cette action est irréversible et le crédit utilisé ne sera pas restitué. Êtes-vous sûr de vouloir continuer ?"
        confirmLabel="Supprimer définitivement"
        cancelLabel="Annuler"
        onConfirm={() => handleConfirmDeleteProperty(deletePropertyConfirm.propertyId)}
        onCancel={() => setDeletePropertyConfirm({ isOpen: false, propertyId: null })}
        variant="danger"
      />
    </>
  );
};

export default EstimatesList;
